import React, { useEffect, useRef, useState } from 'react';
import { matchPath, RouteComponentProps, useLocation, useParams, withRouter } from 'react-router-dom';
import type { Props as ReactSelectProps } from 'react-select';
import Select, { components } from 'react-select';
// import { toast } from 'react-toastify';
import classNames from 'classnames';

// UI Components & Icons
import { IconDefinition, faQuestionCircle } from '@fortawesome/fontawesome-free-solid';
import { ReactComponent as ArrowBackSvg } from '../../../assets/svg/arrow_back.svg';
import { ReactComponent as ChevronDownBlackSvg } from '../../../assets/svg/chevron_down_black.svg';
import SearchIcon from '../../SVG-components/search';
import { AdvancedSearchModal, AdvancedSearchCondition, ADVANCED_SEARCH_PREFIX } from '../../AdvancedSearchModal';
import { Fa } from '../../elements/fa';
import MinimalTile from '../../general/MinimalTile';

// State & Data Management
import { useDispatch, useSelector } from "react-redux";
import * as devicedb from '../../../shared/db/devices-db';
import { iFullStoreState } from "../../../shared/interfaces";
import { Actions as GeneralActions } from '../../../stores/reducers/general-reducers';
import { Actions as ReportActions } from "../../../stores/reducers/report-reducers";
import { addRecentSearch, getRecentSearches } from '../../../shared/recentSearches';

// Styles
import Constants from '../../../shared/constants';
import { sortBySelectStyles } from '../../general/selections-grid/constants';
import styles from './modal-header.module.scss';
import { DashboardSize } from '../../../stores/reducers/dashboardInfo';

interface IHeaderAction {
    fa?: IconDefinition;
    click: () => void;
    title: string;
    disabled?: string | false;
    faPdf?: IconDefinition;
    clickPdf?: () => void;
    titlePdf?: string;
    icon?: string | React.ReactNode;
    isDisabled?: boolean;
}

interface OwnProps {
    action?: IHeaderAction;
    help?: string;
    title: string | JSX.Element;
    style?: React.CSSProperties;
    role?: boolean;
    goBackCallback?: () => void;
    showSelect?: boolean;
    options?: ReactSelectProps;
    handleChange?: (option: ReactSelectProps) => void;
    option?: ReactSelectProps;
    className?: string;
    dashboardSize?: boolean;
    isEditableTitle?: boolean;
    paginateComponent?: React.ReactNode;
    selectStyles?: unknown;
    selectExtraProps?: Record<string, unknown>;
    filterLabel?: string;
    secondSelectComponent?: React.ReactNode;
}

type GoBackProps = {
    children: React.ReactNode;
    goBackCallback?: () => void;
} & RouteComponentProps;

export const GoBack = withRouter(({ history, children, goBackCallback }: GoBackProps) => {
    const location = useLocation();
    const dispatch = useDispatch();

    const topLevelPages = ['/device', '/person', '/tags', '/reports/new/travel'];

    // Do not show go back button for top level pages
    if (topLevelPages.includes(location.pathname)) return null;

    const endWithPaths = ['device', 'person', 'tags'];

    const pathRedirects = [
        { regex: /\/device\/[^/]+/, redirect: '/device' },
        { regex: /\/person\/[^/]+/, redirect: '/person' },
        { regex: /\/tag\/[^/]+/, redirect: '/tags' },
    ];

    const goBackHandler = () => {
        if (goBackCallback) {
            goBackCallback();
        }

        const { pathname } = location;

        const pathEnd = pathname.split('/').filter(Boolean).pop();

        if (pathEnd && endWithPaths.includes(pathEnd) && pathname === `/${pathEnd}`) return;

        const match = pathname.match(/^\/device(?:\/([^/]+))?(?:\/.*)?$/);
        const deviceId = match?.[1];

        if (match) {
          if (deviceId && pathname === `/device/${deviceId}`) {
            history.goBack();
            return;
          }

          if (deviceId) {
            const cleanPath = `/device/${deviceId}`;
            if (pathname !== cleanPath) {
              history.replace(cleanPath);
              return;
            }
          }

          return;
        }

        for (const { regex, redirect } of pathRedirects) {
            if (regex.test(pathname)) {
                history.replace(redirect);
                return;
            }
        }

        if (/^\/reports\/new\/[^/]+$/.test(pathname)) {
            dispatch(ReportActions.RESET_ALL_FILTERS());
            dispatch(ReportActions.RESET_DATES());
            return;
        }

        history.goBack();
    };


    return (
        <button onClick={goBackHandler} className={styles.goBackButton}>
            {children}
        </button>
    );
});

const CustomSingleValue = (props: any) => {
    const { selectProps } = props;
    const isOpen = selectProps.menuIsOpen;
    const label = selectProps.filterLabel;

    return (
      <components.SingleValue {...props}>
        <div className={styles.singleValueBlock}>
          <span>{label}</span>
          <ChevronDownBlackSvg
            className={classNames(styles.singleValueBlock__icon, {
              [styles.singleValueBlock__icon_focused]: isOpen,
            })}
          />
        </div>
      </components.SingleValue>
    );
};

const ModalHeader: React.FC<OwnProps> = ({
    action,
    help,
    style: userStyle = {},
    role,
    goBackCallback,
    showSelect,
    options,
    handleChange,
    option,
    className,
    dashboardSize,
    title,
    isEditableTitle = false,
    paginateComponent = null,
    selectStyles,
    selectExtraProps,
    secondSelectComponent,
}) => {
    const [showHelp, setShowHelp] = useState(false);
    const [editedTitle, setEditedTitle] = useState<any>(title || '');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
    const [recentSearches, setRecentSearches] = useState(getRecentSearches());
    const previousIsSearchVis = useRef(false);
    const lastNonEmptyTermRef = useRef('');

    const hasHeaderButtons = Boolean(paginateComponent || action || help);
    const size = useSelector<iFullStoreState, DashboardSize>((s) => s.dashboardInfo.size);
    const fullSize = size === "OPEN_FULL"
    const isSearchVis = useSelector((state: iFullStoreState) => state.general.showSearch);
    const searchTerm = useSelector((state: iFullStoreState) => state.general.searchTerm);
    const authUser = useSelector((state: iFullStoreState) => state.auth.user);
    const isAdvancedSearchActive = (searchTerm || '').startsWith(ADVANCED_SEARCH_PREFIX);

    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams<{ id: string }>();

    useEffect(() => {
        setEditedTitle(title || '');
    }, [title]);

    useEffect(() => {
      const trimmed = (searchTerm || '').trim();
      if (trimmed.length > 0) {
        lastNonEmptyTermRef.current = trimmed;
      }
    }, [searchTerm]);

    useEffect(() => {
      if (previousIsSearchVis.current && !isSearchVis && lastNonEmptyTermRef.current) {
        addRecentSearch(lastNonEmptyTermRef.current);
        setRecentSearches(getRecentSearches());
      }
      previousIsSearchVis.current = isSearchVis;
    }, [isSearchVis]);

    const isExactDevicePage = !!matchPath(location.pathname, {
      path: "/device/:deviceId",
      exact: true,
    });

    const isDevicesOrPeoplePage = location.pathname === '/device' || location.pathname === '/person';

    const isTagDetailPage = !!matchPath(location.pathname, {
      path: "/tag/:tagId",
      exact: true,
    });

    const shouldDisableSuggestions =
      location.pathname === '/person' ||
      location.pathname === '/tags' ||
      location.pathname.includes('/reports/');

    let filterLabel = 'Filter';

    if (location.pathname.includes('/device')) {
      filterLabel = 'Vehicle';
    } else if (location.pathname.includes('/person')) {
      filterLabel = 'User';
    }

    const handleShowSearchModalWhenValueIsNotEmpty = () => {
        // Device pages should keep the header visible, not show the global search modal
        if (isExactDevicePage) {
            return;
        }
        if (!isSearchVis && searchTerm.length > 0) {
            dispatch(GeneralActions.SHOW_SEARCH);
        }
    }

    const handleShowSearch = (e) => {
        dispatch(GeneralActions.CHANGE_TERM(e.target.value));
        // Show search modal when typing
        if (!isSearchVis && e.target.value.length > 0) {
            dispatch(GeneralActions.SHOW_SEARCH);
        }
        setShowSuggestions(false);
    };

    const handleSearchFocus = () => {
      handleShowSearchModalWhenValueIsNotEmpty();
      const recent = getRecentSearches();
      setRecentSearches(shouldDisableSuggestions ? [] : recent);
      setShowSuggestions(true);
    };

    const handleSearchBlur = () => {
      if (searchTerm && searchTerm.trim().length > 0) {
        addRecentSearch(searchTerm);
        setRecentSearches(getRecentSearches());
      }
      setTimeout(() => setShowSuggestions(false), 200);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && searchTerm && searchTerm.trim().length > 0) {
        addRecentSearch(searchTerm);
        setRecentSearches(getRecentSearches());
        setShowSuggestions(false);
      }
    };

    const handleSuggestionClick = (term: string) => {
      dispatch(GeneralActions.CHANGE_TERM(term));
      if (!isSearchVis && term.length > 0) {
        dispatch(GeneralActions.SHOW_SEARCH);
      }
      addRecentSearch(term);
      setRecentSearches(getRecentSearches());
      setShowSuggestions(false);
    };

    const formatRecentSearchDisplay = (term: string) => {
      if (!term?.startsWith(ADVANCED_SEARCH_PREFIX)) {
        return term;
      }

      try {
        const parsed = JSON.parse(term.slice(ADVANCED_SEARCH_PREFIX.length));
        if (!Array.isArray(parsed) || parsed.length === 0) return 'Search';

        const operatorMap = {
          startsWith: 'starts with',
          contains: 'contains',
          equals: 'equals',
          greaterThan: '>',
          lessThan: '<',
        };

        const summary = parsed
          .map((c: any) => `${c.fieldLabel || c.fieldId} ${operatorMap[c.operator] || c.operator} ${c.value}`)
          .join(' • ');

        return summary;
      } catch {
        return 'Search';
      }
    };

    const handleAdvancedSearch = (conditions: AdvancedSearchCondition[]) => {
      if (!conditions?.length) return;

      const encodedSearch = `${ADVANCED_SEARCH_PREFIX}${JSON.stringify(conditions)}`;
      dispatch(GeneralActions.CHANGE_TERM(encodedSearch));
      if (!isSearchVis) {
        dispatch(GeneralActions.SHOW_SEARCH);
      }
      setShowAdvancedSearch(false);
      setShowSuggestions(false);
    };

    const updateDeviceTitle = async () => {
        if (isEditableTitle && title !== editedTitle.trim() && params?.id) {
            try {
                await devicedb.updateDeviceDetails(authUser)(
                    params?.id,
                    {
                        name: editedTitle.trim(),
                    }
                );
                // toast.success('Device title updated');
            } catch (error) {
                console.error('Failed to update device title:', error);
                // toast.error('Failed to update device title');
            }
        }
    };

    return (
      <>
        {/* Header */}
        <div
          className={classNames(styles.header, {
            [styles.withSelect]: showSelect,
            [styles.pinnedHeader]: isDevicesOrPeoplePage,
          }, className)}
          style={{ ...userStyle }}
        >
          {/* Back button */}
          <GoBack goBackCallback={goBackCallback}>
            <ArrowBackSvg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                color: '#FF7C02',
              }}
            />
          </GoBack>

            {/* Selects */}
            {(showSelect || secondSelectComponent) && (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {showSelect && (
                  <Select
                    onChange={handleChange}
                    options={options}
                    value={option}
                    className={className}
                    styles={selectStyles ?? sortBySelectStyles}
                    components={{
                      SingleValue: CustomSingleValue,
                      DropdownIndicator: () => null,
                    }}
                    isSearchable={false}
                    menuPlacement="auto"
                    filterLabel={filterLabel}
                    {...(selectExtraProps ?? {})}
                  />
                )}
                {secondSelectComponent}
              </div>
            )}

            {/* Title */}
            {isDevicesOrPeoplePage ? (
              // Search only for Devices and People main pages
              (<div
                className={classNames(styles.searchContainer, {
                  [styles.searchContainerDevicePage]: location.pathname === '/device',
                })}
              >
                <div className={styles.searchControl}>
                  <SearchIcon className={styles.searchIcon} />

                  <input
                    className={styles.searchInput}
                    style={isExactDevicePage ? { marginRight: '0px' } : {}}
                    type="text"
                    id="search-input"
                    value={formatRecentSearchDisplay(searchTerm)}
                    onFocus={handleSearchFocus}
                    onChange={handleShowSearch}
                    onBlur={handleSearchBlur}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search"
                  />
                </div>
                {showSuggestions && (
                  <div className={styles.recentSearchesDropdown}>
                    <div className={styles.recentSearchesHeader}>Recent Searches</div>
                    {recentSearches.map((search) => (
                      <div
                        key={search.term}
                        className={styles.recentSearchItem}
                        onClick={() => handleSuggestionClick(search.term)}
                      >
                        <span>{formatRecentSearchDisplay(search.term)}</span>
                      </div>
                    ))}
                    <button
                      type="button"
                      className={styles.advancedSearchLink}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setShowAdvancedSearch(true);
                        setShowSuggestions(false);
                      }}
                    >
                      Advanced Search
                    </button>
                  </div>
                )}
              </div>)
            ) : isEditableTitle ? (
              <div className={styles.editableTitleContainer}>
                {/* Editable title */}
                <input
                  className={classNames(styles.heading, styles.editableInput)}
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onBlur={updateDeviceTitle}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />

                {/* Search */}
                <div className={styles.searchContainer}
                  style={{
                      position: isExactDevicePage ? 'relative' : 'absolute',
                      left: isExactDevicePage ? 'auto' : (fullSize ? '50%' : '87px'),
                      right: 'auto',
                      transform: isExactDevicePage
                        ? 'none'
                        : (fullSize ? 'translate(-50%, -50%)' : 'translate(0%, -50%)'),
                      top: isExactDevicePage ? 'auto' : '59%',
                      zIndex: 100,
                      ...(isExactDevicePage ? { marginLeft: 'auto', marginRight: 0 } : {})
                  }}
                >
                  <SearchIcon className={styles.searchIcon} />

                  <input
                    className={styles.searchInput}
                    type="text"
                    id="search-input"
                    value={formatRecentSearchDisplay(searchTerm)}
                    onFocus={handleSearchFocus}
                    onChange={handleShowSearch}
                    onBlur={handleSearchBlur}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search"
                  />
                  {showSuggestions && (
                    <div className={styles.recentSearchesDropdown}>
                      <div className={styles.recentSearchesHeader}>Recent Searches</div>
                      {recentSearches.map((search) => (
                        <div
                          key={search.term}
                          className={styles.recentSearchItem}
                          onClick={() => handleSuggestionClick(search.term)}
                        >
                          <span>{formatRecentSearchDisplay(search.term)}</span>
                        </div>
                      ))}
                      <button
                        type="button"
                        className={styles.advancedSearchLink}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setShowAdvancedSearch(true);
                          setShowSuggestions(false);
                        }}
                      >
                        Advanced Search
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <h3
                className={classNames(styles.heading, {
                  [styles.heading_leftPosition]: showSelect,
                })}
              >
                {title}
              </h3>
            )}

          {/* Buttons */}
          {hasHeaderButtons ? (
            <div className={styles.buttons}>
              {!role && (
                <div className={styles.row}>
                  {paginateComponent && paginateComponent}

                  {action && <ActionDisplay {...action} />}

                  {help && (
                    <Fa
                      icon={faQuestionCircle}
                      style={{ cursor: 'pointer' }}
                      className="modal-help"
                      title="Show instructions"
                      onClick={() => setShowHelp((prev) => !prev)}
                    />
                  )}
                </div>
              )}
            </div>
          ) : (
            // placeholder to keep the search input aligned when there are no buttons
            (!isTagDetailPage && <div style={{ width: 17.5 }} />)
          )}
        </div>
        <AdvancedSearchModal
          open={showAdvancedSearch}
          onClose={() => setShowAdvancedSearch(false)}
          onSearch={handleAdvancedSearch}
        />
        {/* Help */}
        {showHelp && (
          <div
            className={classNames('help-body no-print', { active: showHelp })}
          >
            <MinimalTile>
              <div style={{ maxWidth: 325 }}>{help}</div>
            </MinimalTile>
          </div>
        )}
      </>
    );
};

export default ModalHeader;

const ActionDisplay = ({
    fa,
    click,
    title = '',
    disabled = false,
    faPdf,
    clickPdf,
    titlePdf = '',
    isDisabled,
    icon,
}: IHeaderAction) => {
    const handleClick = (handler?: () => void) => (disabled ? undefined : handler);

    const baseStyle: React.CSSProperties = {
        fontSize: 20,
        cursor: disabled ? 'default' : 'pointer',
        color: disabled ? Constants.lightGray : 'inherit',
    };

    const pdfStyle: React.CSSProperties = {
        ...baseStyle,
        color: disabled ? Constants.lightGray : 'red',
        marginRight: 10,
    };

    const customIconStyle: React.CSSProperties = {
        ...baseStyle,
        width: 20,
        height: 20,
        position: 'relative',
    };

    return (
        <>
            {/* PDF button */}
            {faPdf && (
                <span
                  title={titlePdf}
                  className="btn-link"
                  onClick={handleClick(clickPdf)}
                  style={pdfStyle}
                >
                  <Fa icon={faPdf} />
                </span>
            )}

            {/* Custom React element as icon */}
            {icon && typeof icon !== 'string' ? (
                <div
                  title={title}
                  className="btn-link"
                  onClick={handleClick(click)}
                  style={customIconStyle}
                >
                  {icon}
                </div>
            ) : (icon || fa) ? (
                <span
                  title={title}
                  className="btn-link"
                  onClick={handleClick(click)}
                  style={baseStyle}
                >

                    {icon ? (
                        <img src={icon as string} width={18} alt="icon" />
                    ) : (
                        fa && <Fa icon={fa} style={isDisabled ? { color: 'white' } : {}} />
                    )}
                </span>
            ) : null}
        </>
    );
};